/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { GenericTableData } from '../../sharedServices/generic-table-data.service';
import { FittingState } from '../../models/enums/FittingState.enum';
import { SearchService } from 'api/services';
import { FittingUnitDto, ManufacturerDto, ManufacturerProductLineDto, FittingDto} from 'api/models';
import { LoaderService } from '../../sharedServices/loader.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';


@Component({
  selector: 'app-search-ui',
  templateUrl: './search-ui.component.html',
  styleUrls: ['./search-ui.component.css']
})

export class SearchUIComponent implements OnInit {

  @Input() searchContentItem = false;

  constructor(
    private readonly searchService: SearchService,
    private readonly fittingDataService: GenericTableData,
    private loaderService: LoaderService) {
    fittingDataService.setTableHeader(this.setColumnHeaders());
  }
  unitsList: FittingUnitDto[];
  unitsFormControl = new FormControl();
  filteredUnits: Observable<FittingUnitDto[]>;
  selectedUnit: FittingUnitDto;
  searchText: string;

  manufacturerList: ManufacturerDto[];
  manufacturerFormControl = new FormControl({ Disabled: true });
  filteredManufacturers: Observable<ManufacturerDto[]>;
  selectedManufacturer: ManufacturerDto;

  productLineList: ManufacturerProductLineDto[];
  productLineFormControl = new FormControl({ Disabled: true });
  filteredProductLines: Observable<ManufacturerProductLineDto[]>;
  selectedProductLine: ManufacturerProductLineDto;

  fittingRadioButtonStateState: FittingState;
  fittingStateOptions = [];
  fittingState: boolean;
  productDetailSearch: FittingDto[];

  @Output() isLoading = new EventEmitter<boolean>();

  columnHeader: any;

  ngOnInit(): void {
    this.resetDataTable();
    this.fittingRadioButtonStateState = FittingState.Continue;
    this.fittingState = true;
    this.fittingStateOptions = Object.keys(FittingState);
    this.manufacturerFormControl.disable();
    this.productLineFormControl.disable();

    this.searchService.getFittingUnits().subscribe(unitList => {
      this.unitsList = unitList;
      this.filteredUnits = this.unitsFormControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterUnits(value))
      );
    });
  }

  setColumnHeaders() {
    if (this.searchContentItem === true) {
      this.columnHeader = {
        imageData: 'Image',
        name: 'Name',
        extendedName: 'Extended Name',
        manufacturer: 'Manufacturer',
        material: 'Material',
        productType: 'Product Type',
        relativeFileLocation: 'Relative File Location',
        specification: 'Specification',
        webServiceId: 'FittingEntryID'}
      } else {
        this.columnHeader =  {
          action: 'Action',
          actionBy: 'Action By',
          unitsName: 'Unit Name',
          productDiscipline: 'Product Discipline',
          productLine: 'Product Line ',
          longDescription: 'Long Description',
          fileName: 'File Name',
          componentGroup: 'Component Group',
          materialAbrv: 'Material Abreviation',
          materialName: 'Material Name',
          manufName: 'Manufacturer Name',
          manufAbrv: 'Manufacturer Abreviation',
          valveClass: 'Valve Class',
          schedule: 'Schedule',
          installType: 'Install Type',
          subProductName: 'Sub-Product Name',
          fittingType: 'Fitting Type',
          numberOfConnections: 'Number of Connections',
        }
      }

    this.fittingDataService.setTableHeader(this.columnHeader);
    return this.columnHeader;
  }

  filterUnits(value): FittingUnitDto[] {
    const filterValue = value === null ? '' : typeof value === 'object' ? value.unitsName.toLocaleLowerCase() : value.toLocaleLowerCase();
    return this.unitsList.filter(unit =>
      unit.unitsName.toLowerCase().includes(filterValue)
    );
  }

  selectUnits($event: MatAutocompleteSelectedEvent) {
    this.selectedUnit = $event.option.value;
    if (this.selectedUnit) {
      this.manufacturerFormControl.disabled ? this.manufacturerFormControl.enable() : this.manufacturerFormControl.reset();
      this.resetProductLineForm();
      this.searchService.getManufacturers({ unitId: this.selectedUnit.unitsOriginID })
        .subscribe(manufacturerList => {
          this.manufacturerList = manufacturerList;

          this.filteredManufacturers = this.manufacturerFormControl.valueChanges.pipe(
            startWith(''),
            map(value => this.filterManufacturers(value))
          );
        });
    } else {
      this.manufacturerFormControl.disable();
      this.resetDataTable();
    }
  }

  filterManufacturers(value): ManufacturerDto[] {
    const filterValue = value === null ? '' : typeof value === 'object' ? value.manufName.toLocaleLowerCase() : value.toLocaleLowerCase();
    return this.manufacturerList.filter(manufacturer =>
      manufacturer.manufName.toLowerCase().includes(filterValue)
    );
  }

  selectManufacturer($event: MatAutocompleteSelectedEvent) {
    this.selectedProductLine = null;
    this.selectedManufacturer = $event.option.value;
    if (this.selectedManufacturer) {
      this.productLineFormControl.disabled ? this.productLineFormControl.enable() : this.productLineFormControl.reset();
      this.resetDataTable();
      this.searchService.getProductLines(
        {
          unitId: this.selectedUnit.unitsOriginID,
          manufacturerId: this.selectedManufacturer.manufID
        }).subscribe(productlineList => {
          this.productLineList = productlineList;
          this.filteredProductLines = this.productLineFormControl.valueChanges.pipe(
            startWith(''),
            map(value => this.filterproductLine(value))
          );
        });
    } else {
      this.productLineFormControl.disable();
      this.resetDataTable();
    }
  }

  resetProductLineForm() {
    this.productLineFormControl.reset();
    this.productLineFormControl.disable();
    this.resetDataTable();
  }

  filterproductLine(value): ManufacturerProductLineDto[] {
    const filterValue = value === null ? '' : typeof value === 'object' ? value.productLine.toLocaleLowerCase() : value.toLocaleLowerCase();
    return this.productLineList.filter(productLine =>
      productLine.productLine.toLowerCase().includes(filterValue)
    );
  }

  selectProductLine($event: MatAutocompleteSelectedEvent) {
    this.selectedProductLine = $event.option.value;
    this.resetDataTable();
  }

  async searchProductDetails() {
    this.isLoading.emit(true);
    this.searchService.getFittings({
      unitId: this.selectedUnit.unitsOriginID,
      productLineId: this.selectedProductLine.manufProductLineID,
      isContinued: this.fittingState
    }).subscribe(r => {
      this.fittingDataService.setTableDataList(r);
      this.isLoading.emit(false);
    });
  }

  checkFittingState() {
    return this.fittingState === true ? false : true;
  }

  setFittingState(event) {
    this.fittingState = event.checked ? false : true;
    this.resetDataTable();
  }

  clearProductLine() {
    this.selectedProductLine = null;
    this.productLineFormControl.reset();
  }

  checkProductLine() {
    return this.selectedProductLine !== undefined && this.selectedProductLine !== null ? false : true;
  }

  clearManufacturer() {
    this.selectedManufacturer = null;
    this.manufacturerFormControl.reset();
    this.clearProductLine();
    this.productLineFormControl.disable();
  }

  clearUnits() {
    this.selectedUnit = null;
    this.clearManufacturer();
    this.unitsFormControl.reset();
    this.manufacturerFormControl.disable();
    this.productLineFormControl.disable();
  }

  displayUnitName(unit: FittingUnitDto) {
    return unit ? unit.unitsName : '';
  }

  displayManufacturerName(manufacturer: ManufacturerDto) {
    return manufacturer ? manufacturer.manufName : '';
  }

  displayProductLineName(productLine: ManufacturerProductLineDto) {
    return productLine ? productLine.productLine : '';
  }

  isSearchButtonDisabled() {
    return this.selectedProductLine === null || this.selectedProductLine === undefined
      || this.selectManufacturer === null || this.selectUnits === undefined
      || this.selectUnits === null || this.selectedUnit === undefined
      ;
  }

  resetDataTable() {
    this.fittingDataService.setTableDataList(null);
  }
}
